import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from '@/i18n';
import {
	persistThemeModePreference,
	resolveThemeMode,
	SUPPORTED_THEME_MODES,
} from '@/utils/theme-mode';

export const ACCOUNT_PREFERENCES_STORAGE_PREFIX =
	'sapphire_organizer_account_preferences';

const SUPPORTED_DATE_FORMATS = ['yyyy-MM-dd', 'yyyy/MM/dd', 'MM/dd/yyyy'];

const resolveBrowserTimeZone = () => {
	try {
		return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Taipei';
	} catch (error) {
		return 'Asia/Taipei';
	}
};

export const createDefaultAccountBrowserPreferences = () => ({
	language: DEFAULT_LANGUAGE,
	themeMode: 'light',
	timeZone: resolveBrowserTimeZone(),
	dateFormat: 'yyyy-MM-dd',
	showTopbarNotifications: true,
});

const createCurrentAccountBrowserPreferences = store => {
	const defaults = createDefaultAccountBrowserPreferences();
	const currentLanguage = store?.state?.settings?.config?.lang;
	const currentThemeMode = store?.state?.theme?.themeMode;

	return {
		...defaults,
		language: SUPPORTED_LANGUAGES.includes(currentLanguage)
			? currentLanguage
			: defaults.language,
		themeMode: SUPPORTED_THEME_MODES.includes(currentThemeMode)
			? currentThemeMode
			: defaults.themeMode,
	};
};

const normalizeScopeId = orzuid => `${orzuid || ''}`.trim().toLowerCase();

export const getAccountPreferencesStorageKey = orzuid => {
	const scopeId = normalizeScopeId(orzuid);
	return scopeId
		? `${ACCOUNT_PREFERENCES_STORAGE_PREFIX}.${scopeId}`
		: '';
};

const isValidTimeZone = timeZone => {
	if (!timeZone || typeof timeZone !== 'string') return false;

	try {
		Intl.DateTimeFormat('en', { timeZone }).format(new Date());
		return true;
	} catch (error) {
		return false;
	}
};

const sanitizeAccountBrowserPreferences = value => {
	const defaults = createDefaultAccountBrowserPreferences();
	const candidate = value && typeof value === 'object' ? value : {};

	return {
		language: SUPPORTED_LANGUAGES.includes(candidate.language)
			? candidate.language
			: defaults.language,
		themeMode: SUPPORTED_THEME_MODES.includes(candidate.themeMode)
			? candidate.themeMode
			: defaults.themeMode,
		timeZone: isValidTimeZone(candidate.timeZone)
			? candidate.timeZone
			: defaults.timeZone,
		dateFormat: SUPPORTED_DATE_FORMATS.includes(candidate.dateFormat)
			? candidate.dateFormat
			: defaults.dateFormat,
		showTopbarNotifications:
			candidate.showTopbarNotifications !== false,
	};
};

const readLocalPreferences = orzuid => {
	const storageKey = getAccountPreferencesStorageKey(orzuid);
	if (!storageKey || typeof window === 'undefined') return null;

	try {
		const raw = window.localStorage.getItem(storageKey);
		return raw ? sanitizeAccountBrowserPreferences(JSON.parse(raw)) : null;
	} catch (error) {
		console.warn(
			'account-preferences, read-local-preferences failed',
			error
		);
		return null;
	}
};

const getStorePreferences = (store, orzuid) => {
	const scopeId = normalizeScopeId(orzuid);
	const stored =
		store?.state?.settings?.config?.accountPreferences?.[scopeId];
	return stored ? sanitizeAccountBrowserPreferences(stored) : null;
};

const commitStorePreferences = (store, orzuid, preferences) => {
	const scopeId = normalizeScopeId(orzuid);
	if (!store || !scopeId) return;

	store.commit('setAccountPreferences', {
		orzuid: scopeId,
		value: preferences,
	});
};

export const hasStoredAccountBrowserPreferences = orzuid => {
	const storageKey = getAccountPreferencesStorageKey(orzuid);
	return Boolean(
		storageKey &&
			typeof window !== 'undefined' &&
			window.localStorage.getItem(storageKey)
	);
};

export const loadAccountBrowserPreferences = (store, orzuid) => {
	const preferences =
		readLocalPreferences(orzuid) ||
		getStorePreferences(store, orzuid) ||
		createCurrentAccountBrowserPreferences(store);

	commitStorePreferences(store, orzuid, preferences);
	return preferences;
};

export const persistAccountBrowserPreferences = (
	store,
	orzuid,
	preferences
) => {
	const storageKey = getAccountPreferencesStorageKey(orzuid);
	const sanitized = sanitizeAccountBrowserPreferences(preferences);

	commitStorePreferences(store, orzuid, sanitized);
	persistThemeModePreference(sanitized.themeMode);
	if (storageKey && typeof window !== 'undefined') {
		window.localStorage.setItem(storageKey, JSON.stringify(sanitized));
	}

	return sanitized;
};

export const updateAccountBrowserPreference = (
	store,
	orzuid,
	name,
	value
) => {
	const current = loadAccountBrowserPreferences(store, orzuid);
	return persistAccountBrowserPreferences(store, orzuid, {
		...current,
		[name]: value,
	});
};

export const applyAccountBrowserPreferences = async ({
	store,
	orzuid,
	i18next,
}) => {
	const preferences = loadAccountBrowserPreferences(store, orzuid);
	const effectiveThemeMode = resolveThemeMode(preferences.themeMode);

	store.commit('changeSetting', {
		group: 'config',
		name: 'lang',
		value: preferences.language,
	});
	await i18next?.changeLanguage?.(preferences.language);
	persistThemeModePreference(preferences.themeMode);
	await store.dispatch('changeThemeMode', effectiveThemeMode);

	if (typeof document !== 'undefined') {
		document.documentElement.setAttribute(
			'data-bs-theme',
			effectiveThemeMode
		);
		document.body?.setAttribute('data-theme', effectiveThemeMode);
	}

	return preferences;
};

const parseDateValue = value => {
	if (!value) return null;
	if (value instanceof Date) return value;

	const normalized =
		typeof value === 'string' ? value.replace(' ', 'T') : value;
	const date = new Date(normalized);
	return Number.isNaN(date.getTime()) ? null : date;
};

const localeForLanguage = language => {
	const localeMap = {
		zh_tw: 'zh-TW',
		zh_cn: 'zh-CN',
		en: 'en-US',
		ja: 'ja-JP',
		ko: 'ko-KR',
	};
	return localeMap[language] || 'zh-TW';
};

export const formatAccountPreferenceDateTime = (
	value,
	preferences,
	language
) => {
	const date = parseDateValue(value);
	if (!date) return value || '';

	const safePreferences = sanitizeAccountBrowserPreferences(preferences);
	try {
		const parts = new Intl.DateTimeFormat(
			localeForLanguage(language),
			{
				timeZone: safePreferences.timeZone,
				year: 'numeric',
				month: '2-digit',
				day: '2-digit',
				hour: '2-digit',
				minute: '2-digit',
				second: '2-digit',
				hourCycle: 'h23',
			}
		).formatToParts(date);
		const partMap = Object.fromEntries(
			parts
				.filter(part => part.type !== 'literal')
				.map(part => [part.type, part.value])
		);
		const dateParts = {
			'yyyy-MM-dd': `${partMap.year}-${partMap.month}-${partMap.day}`,
			'yyyy/MM/dd': `${partMap.year}/${partMap.month}/${partMap.day}`,
			'MM/dd/yyyy': `${partMap.month}/${partMap.day}/${partMap.year}`,
		};
		const time = `${partMap.hour}:${partMap.minute}:${partMap.second}`;

		return `${dateParts[safePreferences.dateFormat]} ${time}`;
	} catch (error) {
		return value || '';
	}
};
